import { mergeAssociatedCampaignEntries } from './prospectAssociatedCampaignsDisplay.js';

/** Auto-follow empty pages that still carry a continuation cursor. */
export const MAX_EMPTY_PAGE_CONTINUATIONS = 8;

/**
 * Production loading loop for associated campaigns.
 *
 * Keeps a bounded automatic empty-page continuation, then stops so the UI can
 * offer manual continuation whenever nextPageToken remains. Distinguishes:
 * - genuinely exhausted (no entries, no token) → status "empty"
 * - no visible rows yet, more pages available → status "ready" + emptyWithMore
 * - visible rows (with or without more) → status "ready"
 *
 * Avoids repeated-cursor auto-loops and preserves already-loaded entries.
 */
export async function runAssociatedCampaignsLoadLoop({
  fetchPage,
  initialEntries = [],
  pageToken = null,
  maxEmptyContinuations = MAX_EMPTY_PAGE_CONTINUATIONS,
  mergeEntries = mergeAssociatedCampaignEntries,
  shouldAbort = () => false,
} = {}) {
  if (typeof fetchPage !== 'function') {
    throw new TypeError('fetchPage is required');
  }

  let workingEntries = Array.isArray(initialEntries) ? [...initialEntries] : [];
  let token = pageToken || null;
  let coverage = null;
  let emptyContinuations = 0;
  let previousAutoCursor = null;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (shouldAbort()) {
      return { aborted: true };
    }

    const data = await fetchPage({ pageToken: token || undefined });
    if (shouldAbort()) {
      return { aborted: true };
    }

    const rows = Array.isArray(data?.entries) ? data.entries : [];
    coverage = data?.orderingCoverage || coverage;
    workingEntries = mergeEntries(workingEntries, rows);
    const nextToken = typeof data?.nextPageToken === 'string' && data.nextPageToken
      ? data.nextPageToken
      : null;

    if (
      rows.length === 0 &&
      nextToken &&
      previousAutoCursor != null &&
      nextToken === previousAutoCursor
    ) {
      // Same cursor after an empty page — stop auto-continue to avoid a loop.
      token = nextToken;
      break;
    }

    token = nextToken;

    if (rows.length === 0 && token && emptyContinuations < maxEmptyContinuations) {
      emptyContinuations += 1;
      previousAutoCursor = token;
      continue;
    }
    break;
  }

  const exhausted = workingEntries.length === 0 && !token;
  const emptyWithMore = workingEntries.length === 0 && Boolean(token);

  return {
    aborted: false,
    entries: workingEntries,
    nextPageToken: token,
    orderingCoverage: coverage,
    status: exhausted ? 'empty' : 'ready',
    emptyWithMore,
    exhausted,
    canManualContinue: Boolean(token),
    emptyContinuations,
  };
}
